import React, { useEffect, useState } from 'react';

import { pick } from 'lodash';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useMediaQuery } from 'react-responsive';
import TagManager from 'react-gtm-module';

import GoogleLogin from 'features/session/LoginGoogle';

import Text from 'common/components/Text/Text';
import BannerCarousel from 'features/session/BannerCarousel';
import {
  useAddLoginMutation,
  useAddPostulationMutation,
  useGetProfileQuery,
  useGetUserQuery,
} from 'services/exploreApi';
import { useAppDispatch } from 'app/hooks';
import {
  loginSuccess,
  setFilters,
  setGoogleToken,
  setProfile,
} from 'features/session/sessionSlice';

import styles from './Login.module.scss';
import { Logo } from 'assets/landing/index';
import 'react-phone-input-2/lib/style.css';
import { ReactComponent as CloseIcon } from 'assets/profile/close.svg';
import { CommonHash, User } from 'app/types';
import { setApplicationStarted, setLastStepDone } from 'features/user/userSlice';

const LoginSchema = yup.object().shape({
  email: yup.string().email().required().label('Email'),
  password: yup.string().max(32).required().label('Password'),
});

interface LoginInfo {
  email: string;
  password: string;
}

const Login = (): React.ReactElement => {
  const [addLogin, { isLoading }] = useAddLoginMutation();
  const [submitPostulation, { isLoading: postulationLoading }] = useAddPostulationMutation();
  const [token, setToken] = useState<string | undefined>(undefined);
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [personalStatementFlow, setPersonalStatementFlow] = useState<boolean>(
    location.pathname.includes('flow=personal-statement-builder')
  );
  const [userData, setUserData] = useState<CommonHash>({});
  const {
    data: loadedUser,
    isLoading: isUserLoading,
    isError,
    isSuccess,
  } = useGetUserQuery(token || '', {
    skip: !token,
  });
  const {
    data: loadedProfile,
    isLoading: isProfileLoading,
    isError: noProfile,
    isSuccess: hasProfile,
  } = useGetProfileQuery(email || '', {
    skip: !email,
  });
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [warningAdvice, setWarningAdvice] = useState<string>('');
  const [googleSuccess, setGoogleSuccess] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const handleApply = (programId: number) => {
    const values = { program_id: programId };
    submitPostulation(values).then((resp: any) => {
      dispatch(setApplicationStarted(true));
      dispatch(setLastStepDone(6));
      navigate(`/home/applications/${resp.data.data.id}`);
    });
  };
  const programIdToApply = searchParams.get('apply_pid');

  const formik = useFormik<LoginInfo>({
    initialValues: { email: '', password: '' },
    validationSchema: LoginSchema,
    onSubmit: (loginInfo: LoginInfo) => {
      addLogin({ user: loginInfo })
        .then((resp: CommonHash) => {
          setWarningAdvice('');
          const user = {
            ...resp.data.response,
            accessToken: resp.data.auth,
          } as User;
          dispatch(loginSuccess({ user }));
          setEmail(formik.values.email);
          if (programIdToApply) {
            setTimeout(() => {
              // navigate(detailUrl('programs', parseInt(programIdToApply, 10), programNameToApply));
              handleApply(parseInt(programIdToApply, 10));
            }, 100);
          } else if (personalStatementFlow) {
            setTimeout(() => {
              // navigate(detailUrl('programs', parseInt(programIdToApply, 10), programNameToApply));
              navigate('/personal-statement-builder');
            }, 50);
          }
        })
        .catch((): void => {
          setWarningAdvice('Invalid email or password, please try again');
        });
    },
  });

  const sendGTMEvents = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'ga_event',
        eventName: googleSuccess ? 'user_login_google' : 'user_login_normal',
      },
    });
  };

  useEffect(() => {
    if (googleSuccess) {
      setToken(userData['token']);
    }
  }, [googleSuccess]);

  useEffect(() => {
    if (isSuccess) {
      addLogin({ user: { provider: 'google_oauth2', token: userData['token'] } }).then(
        (resp: CommonHash) => {
          const user = {
            ...resp.data.response,
            accessToken: resp.data.auth,
          } as User;
          dispatch(loginSuccess({ user }));
          setEmail(resp.data.response.email);
          if (programIdToApply) {
            setTimeout(() => {
              // navigate(detailUrl('programs', parseInt(programIdToApply, 10), programNameToApply));
              handleApply(parseInt(programIdToApply, 10));
            }, 50);
          } else if (personalStatementFlow) {
            setTimeout(() => {
              navigate('/personal-statement-builder');
            }, 50);
          }
        }
      );
    } else if (isError) {
      dispatch(setGoogleToken({ token: userData['token'] }));
      navigate('/signup?google_login=1');
    }
  }, [loadedUser, isError]);

  useEffect(() => {
    sendGTMEvents();
    if (hasProfile) {
      if (!isProfileLoading && loadedProfile?.data) {
        dispatch(setFilters({ order_by_scoring: 'desc' }));
        dispatch(setProfile(loadedProfile.data));
      }
    }
    if ((noProfile || hasProfile) && !programIdToApply && !personalStatementFlow) navigate('/home');
  }, [isProfileLoading, noProfile, hasProfile]);

  return (
    <div className={isMobile ? '' : styles.desktopContainer}>
      {!isMobile && (
        <div className={styles.closeModal}>
          <CloseIcon className={styles.closeIcon} onClick={() => navigate(-1)} />
        </div>
      )}
      <div className={isMobile ? '' : styles.desktopBox}>
        <div className={styles.signupContainer}>
          {isMobile && (
            <div className={styles.prevPageIcon} onClick={() => navigate(-1)}>
              <i className="bi bi-chevron-left"></i>
            </div>
          )}
          {isMobile && <img src={Logo} alt="logo" className={styles.logoContainer} />}
          <Text size={24} weight={700}>
            Welcome back
          </Text>
          <GoogleLogin
            googleSuccess={googleSuccess}
            setUserData={setUserData}
            setGoogleSuccess={setGoogleSuccess}
          />
          <div className={styles.divider}>
            <ul />
            <Text size={10} color="secondary">
              OR
            </Text>
            <ul />
          </div>
          <form onSubmit={formik.handleSubmit} id="loginForm">
            <div className={styles.inputGroup}>
              <Text size={14} weight={500} inlineStyle={{ marginBottom: '5px' }} color="primary">
                Email
              </Text>
              <input
                className={`form-control ${
                  formik.errors.email && formik.touched.email && styles.warningInput
                }`}
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formik.values.email}
                onChange={formik.handleChange}
              />
              {formik.errors.email && formik.touched.email && (
                <p className={styles.warningLabel}>{formik.errors.email}</p>
              )}
            </div>
            <div className={styles.inputGroup}>
              <Text size={14} weight={500} inlineStyle={{ marginBottom: '5px' }} color="primary">
                Password
              </Text>
              <input
                className={`form-control ${
                  formik.errors.password && formik.touched.password && styles.warningInput
                }`}
                type="password"
                name="password"
                placeholder="******"
                value={formik.values.password}
                onChange={formik.handleChange}
              />
              {formik.errors.password && formik.touched.password && (
                <p className={styles.warningLabel}>{formik.errors.password}</p>
              )}
            </div>
            <div className={`form-check mt-2 ${styles.formGroup}`}>
              <div>
                <input
                  id="remember_me"
                  className="form-check-input"
                  type="checkbox"
                  name="flexRadioDefault"
                />
                <label className={`form-check-label ${styles.formLabel}`} htmlFor="remember_me">
                  Remember me
                </label>
              </div>
              {/* <div className={styles.passwordLink}>Forgot password?</div> */}
            </div>
            {warningAdvice && <label className={styles.warningLabel}>{warningAdvice}</label>}
            <button
              className={styles.nextButton}
              type="submit"
              disabled={isLoading || isUserLoading ? true : false}
            >
              {isLoading || isUserLoading || postulationLoading ? (
                <div className={`spinner-border ${styles.loading}`} role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                'Log in'
              )}
            </button>
          </form>
          <div className={styles.bottomText}>
            <Text size={14} color="secondary">
              Don&apos;t have an account?{' '}
            </Text>
            <div
              className={styles.loginLink}
              onClick={() =>
                !personalStatementFlow
                  ? navigate('/signup')
                  : navigate('/signup/flow=personal-statement-builder')
              }
            >
              Join us
            </div>
          </div>
        </div>
        {!isMobile && <BannerCarousel />}
      </div>
    </div>
  );
};

export default Login;
